import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, Typography, Button } from "antd";
import { UnorderedListOutlined } from "@ant-design/icons";
import { Plus, Repeat, Target, LineChart, Layers } from "lucide-react";

const { Title, Paragraph } = Typography;

interface CardData {
  title: string;
  icon: React.ComponentType<any>;
  description: string;
  gradientFrom: string;
  gradientTo: string;
  iconGradient: string;
}

const cardData: CardData[] = [
  {
    title: "One Dataset, Two Engines",
    icon: Layers,
    description:
      "Upload one dataset and Causal Loop runs Bid Optimizer and Geo Lift (Synthetic Control) together - no need to set up and reconcile two separate studies. Prefer a true randomized split instead? Use Causal Loop → With RCT.",
    gradientFrom: "#3b82f6",
    gradientTo: "#8b5cf6",
    iconGradient: "linear-gradient(135deg, #3b82f6, #8b5cf6)",
  },
  {
    title: "Audience-Level Bidding",
    icon: Target,
    description:
      "Find the audience segments worth bidding harder on, with a bid multiplier computed straight from your own first-party data.",
    gradientFrom: "#10b981",
    gradientTo: "#06b6d4",
    iconGradient: "linear-gradient(135deg, #10b981, #06b6d4)",
  },
  {
    title: "Geo Incrementality, Proven",
    icon: LineChart,
    description:
      "Design a geo holdout test with power analysis using a synthetic control model, then measure the real lift your campaign drove against it - no need for a live randomized Control group.",
    gradientFrom: "#f59e0b",
    gradientTo: "#ef4444",
    iconGradient: "linear-gradient(135deg, #f59e0b, #ef4444)",
  },
  {
    title: "Close the Loop",
    icon: Repeat,
    description:
      "Apply the winning bids straight to your test geo and watch the incrementality results roll in from the same study.",
    gradientFrom: "#8b5cf6",
    gradientTo: "#ec4899",
    iconGradient: "linear-gradient(135deg, #8b5cf6, #ec4899)",
  },
];

const CausalLoopLanding: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [animatedCards, setAnimatedCards] = useState<boolean[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedCards(new Array(cardData.length).fill(true));
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-12 md:px-6 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse"></div>
        <div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse"
          style={{ animationDelay: "2s" }}
        ></div>
      </div>

      <div className="relative z-10">
        <div className="text-center mb-12">
          <Title
            level={1}
            className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-6"
          >
            Causal Loop - With Synthetic Control
          </Title>
        </div>

        <div className="text-center flex flex-col sm:flex-row justify-center items-center gap-4 mb-12">
          <Button
            onClick={() => navigate("/causal-loop/list")}
            icon={<UnorderedListOutlined />}
            size="large"
            className="flex items-center gap-2 hover:border-blue-400 hover:text-blue-600 transition-all duration-300 shadow-md hover:shadow-lg"
          >
            View Past Studies
          </Button>
          <Button
            onClick={() => navigate("/jobs/CausalLoop")}
            type="primary"
            icon={<Plus className="h-4 w-4" />}
            size="large"
            className="bg-gradient-to-r from-blue-600 to-purple-600 border-none hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          >
            Run Causal Loop (Synthetic Control)
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 mx-6 xl:grid-cols-2 gap-5">
          {cardData.map((card, index) => {
            const IconComponent = card.icon;
            const isAnimated = animatedCards[index];

            return (
              <Card
                key={index}
                className={`
                  shadow-lg hover:shadow-2xl rounded-2xl border border-gray-200
                  relative group overflow-hidden transition-all duration-700
                  transform hover:scale-105 hover:-translate-y-2
                  ${
                    isAnimated
                      ? "animate-in slide-in-from-bottom-4 fade-in"
                      : "opacity-0"
                  }
                `}
                style={{
                  animationDelay: `${index * 150}ms`,
                  animationDuration: "800ms",
                  animationFillMode: "forwards",
                }}
                bodyStyle={{ padding: "24px" }}
              >
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div
                    className="absolute inset-0"
                    style={{
                      background: `linear-gradient(135deg, ${card.gradientFrom}08, ${card.gradientTo}08)`,
                    }}
                  />
                </div>

                <div
                  className="absolute top-0 left-0 right-0 h-1 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-700 ease-out"
                  style={{ background: card.iconGradient }}
                />

                <div className="flex items-center gap-4 mb-4 relative z-10">
                  <div
                    className="p-3 rounded-xl transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 shadow-md"
                    style={{
                      background: card.iconGradient,
                      boxShadow: `0 4px 20px ${card.gradientFrom}40`,
                    }}
                  >
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>

                  <Title
                    level={4}
                    className="text-gray-800 font-semibold mb-0 group-hover:text-gray-900 transition-colors duration-300"
                  >
                    {card.title}
                  </Title>
                </div>

                <div className="relative z-10">
                  <Paragraph className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                    {card.description}
                  </Paragraph>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CausalLoopLanding;
